'use client'

import * as React from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { cn } from '../utils'
import { useMediaQuery } from '../hooks/use-media-query'

function TooltipProvider({
  delayDuration = 180, // Slight delay to account for quick mouse movement over elements (same as ember app)
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot='tooltip-provider'
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot='tooltip' {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot='tooltip-trigger' {...props} />
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot='tooltip-content'
        sideOffset={sideOffset}
        className={cn(
          'bg-popover-foreground text-popover animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className='bg-popover-foreground fill-popover-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]' />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

/** Tailwind breakpoints a label can collapse below. */
type CollapseBreakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

/**
 * Matches the Tailwind default breakpoints — there are no `--breakpoint-*`
 * overrides in `globals.css`, so these must be kept in sync with Tailwind's
 * defaults rather than read from the theme.
 */
const COLLAPSE_QUERIES: Record<CollapseBreakpoint, string> = {
  sm: '(width < 40rem)',
  md: '(width < 48rem)',
  lg: '(width < 64rem)',
  xl: '(width < 80rem)',
  '2xl': '(width < 96rem)',
}

/**
 * Static per-breakpoint class strings — Tailwind cannot generate class names
 * from runtime values, so each variant has to appear literally in the source.
 *
 * `sr-only` (not `hidden`) keeps the label in the accessibility tree while it is
 * visually collapsed, so the control retains its accessible name and needs no
 * `aria-label`.
 */
const COLLAPSED_LABEL_CLASSES: Record<CollapseBreakpoint, string> = {
  sm: 'sr-only sm:not-sr-only',
  md: 'sr-only md:not-sr-only',
  lg: 'sr-only lg:not-sr-only',
  xl: 'sr-only xl:not-sr-only',
  '2xl': 'sr-only 2xl:not-sr-only',
}

const CollapseBreakpointContext = React.createContext<CollapseBreakpoint | undefined>(undefined)

type CollapsibleLabelProps = React.ComponentProps<'span'> & {
  /**
   * Breakpoint to collapse below. Defaults to the `collapseBelow` of the
   * enclosing `TooltipElement`, so the breakpoint is declared in one place.
   */
  below?: CollapseBreakpoint
}

/**
 * A label that is visible at wide viewports and visually collapses to
 * screen-reader-only below a breakpoint, leaving an icon-only control.
 *
 * Inside a `TooltipElement` with `collapseBelow` set, the tooltip takes over as
 * the visible label exactly when this collapses.
 */
function CollapsibleLabel({ below, className, ...props }: CollapsibleLabelProps) {
  const inheritedBreakpoint = React.useContext(CollapseBreakpointContext)
  const breakpoint = below ?? inheritedBreakpoint

  return (
    <span
      data-slot='collapsible-label'
      className={cn(breakpoint && COLLAPSED_LABEL_CLASSES[breakpoint], className)}
      {...props}
    />
  )
}

type TooltipElementProps = {
  text: React.ReactNode
  children: React.ReactElement
  sideOffset?: number
  className?: string
  /**
   * Only show the tooltip below this breakpoint, and collapse any nested
   * `CollapsibleLabel` at the same point — so the tooltip appears only when the
   * label it duplicates is no longer visible, never alongside it.
   *
   * When set, the tooltip content is `aria-hidden` because the collapsed label
   * already provides the accessible name and Radix would otherwise announce the
   * same text again via `aria-describedby`.
   */
  collapseBelow?: CollapseBreakpoint
  ariaHidden?: boolean
}

const TooltipElement = ({
  text,
  children,
  sideOffset = 0,
  className,
  collapseBelow,
  ariaHidden = false,
}: TooltipElementProps) => {
  const isCollapsed = useMediaQuery(collapseBelow && COLLAPSE_QUERIES[collapseBelow])

  return (
    <CollapseBreakpointContext.Provider value={collapseBelow}>
      <Tooltip open={collapseBelow ? (isCollapsed ? undefined : false) : undefined}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        {text && (
          <TooltipContent sideOffset={sideOffset} className={className}>
            {collapseBelow || ariaHidden ? <span aria-hidden='true'>{text}</span> : text}
          </TooltipContent>
        )}
      </Tooltip>
    </CollapseBreakpointContext.Provider>
  )
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipElement,
  CollapsibleLabel,
}
export type { CollapseBreakpoint }
